import useAutoRefresh from "@/hooks/useAutoRefresh";
import { useEffect, useState, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { startCoinPayment } from "@/lib/coinpay";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Pill } from "@/components/perf";
import Avatar from "@/components/Avatar";
import ServiceFeeNote from "@/components/ServiceFeeNote";
import { toast } from "sonner";
import { Trophy, Search, X, MapPin, Loader2, ArrowLeft, Crown, CheckCircle2, Hourglass, Users, Check, Building2, Lock } from "lucide-react";

const LEVELS = [
  { key: "Low Intermediate", color: "#22C55E" },
  { key: "Intermediate", color: "#EAB308" },
  { key: "High Intermediate", color: "#F97316" },
  { key: "Advanced", color: "#EF4444" },
  { key: "Pro", color: "#A855F7" },
];

export default function LeagueRegister() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [reg, setReg] = useState(null);
  const [teamName, setTeamName] = useState("");
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [partner, setPartner] = useState(null);
  const [city, setCity] = useState("");
  const [stateVal, setStateVal] = useState("");
  const [facility, setFacility] = useState("");
  const [levels, setLevels] = useState([]);
  const [busy, setBusy] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [params, setParams] = useSearchParams();

  const load = useCallback(async () => {
    const { data } = await api.get("/bpl/registration");
    setReg(data);
  }, []);
  useEffect(() => { load(); }, [load]);
  useAutoRefresh(load);

  const pollStatus = useCallback(async (sessionId, attempt = 0) => {
    if (attempt >= 6) { setVerifying(false); toast.error("Payment status check timed out. Check back shortly."); return; }
    try {
      const { data } = await api.get(`/payments/status/${sessionId}`);
      if (data.payment_status === "paid") {
        setVerifying(false);
        toast.success("You're rostered! Registration submitted for approval.");
        load();
        return;
      }
      if (data.status === "expired") { setVerifying(false); toast.error("Payment session expired."); return; }
      setTimeout(() => pollStatus(sessionId, attempt + 1), 2000);
    } catch { setVerifying(false); toast.error("Could not verify payment."); }
  }, [load]);

  useEffect(() => {
    const sid = params.get("session_id");
    if (sid) {
      setVerifying(true);
      pollStatus(sid);
      params.delete("session_id");
      setParams(params, { replace: true });
    }
  }, []); // eslint-disable-line

  useEffect(() => {
    if (!query.trim()) { setResults([]); return; }
    setSearching(true);
    const t = setTimeout(async () => {
      try { const { data } = await api.get("/members/search", { params: { q: query } }); setResults(data); }
      finally { setSearching(false); }
    }, 300);
    return () => clearTimeout(t);
  }, [query]);

  const toggleLevel = (k) => setLevels((l) => (l.includes(k) ? l.filter((x) => x !== k) : [...l, k]));

  const joinLeague = async (withCoin = false) => {
    if (!teamName.trim()) return toast.error("Enter your team name");
    if (!city.trim() || !stateVal.trim()) return toast.error("Enter your city and state");
    if (!facility.trim()) return toast.error("Enter your facility");
    if (levels.length === 0) return toast.error("Check at least one level that applies");
    setBusy(true);
    try {
      if (withCoin) {
        const ok = await startCoinPayment(navigate, { kind: "league_entry", payload: {
          team_name: teamName, partner_id: partner?.id || null, city, state: stateVal, facility, levels,
          origin_url: window.location.origin } });
        if (!ok) setBusy(false);
        return;
      }
      const { data } = await api.post("/bpl/register/checkout", {
        team_name: teamName, partner_id: partner?.id || null, origin_url: window.location.origin,
        city, state: stateVal, facility, levels,
      });
      window.location.href = data.url;
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
      setBusy(false);
    }
  };

  const isVip = ["vip", "ace"].includes(user?.membership);
  const r = reg?.registration;

  return (
    <div className="max-w-2xl mx-auto">
      {verifying && (
        <div className="fixed inset-0 z-[80] bg-black/80 flex items-center justify-center">
          <div className="bg-pb-surface border border-pb-cyan p-8 flex flex-col items-center gap-3">
            <Loader2 className="w-8 h-8 text-pb-cyan animate-spin" />
            <span className="font-display font-bold uppercase tracking-widest">Verifying Payment…</span>
          </div>
        </div>
      )}

      <button data-testid="back-to-league-btn" onClick={() => navigate("/league")}
        className="flex items-center gap-2 text-xs uppercase tracking-widest text-zinc-500 hover:text-pb-red transition-colors mb-6">
        <ArrowLeft className="w-4 h-4" /> Back to League
      </button>

      <PageHeader ck="page.leagueregister" kicker="BPL · Upcoming League Registration"
        title="League Roster"
        subtitle="Lock in your spot for the upcoming Bullpen Performance League. Register solo or with a partner." />

      {!isVip ? (
        <div className="bg-pb-surface border border-pb-red/40 p-6 text-center" data-testid="league-reg-vip-gate">
          <Crown className="w-8 h-8 text-pb-red mx-auto mb-3" />
          <div className="font-display font-bold uppercase text-xl mb-1">VIP & ACE Members Only</div>
          <p className="text-sm text-zinc-400 mb-4">League competition requires a VIP ($1/mo) or ACE ($8/mo) membership.</p>
          <button data-testid="league-reg-upgrade-btn" onClick={() => navigate(`/upgrade?reason=${encodeURIComponent("League registration requires a VIP or ACE membership.")}`)} className="bg-pb-red text-black font-bold uppercase tracking-widest px-6 py-3 hover:bg-[#BEF264] active:scale-95 transition-all">Upgrade to VIP</button>
        </div>
      ) : reg?.registered ? (
        <div className="bg-pb-surface border border-pb-cyan/40 p-6" data-testid="league-reg-status">
          <div className="flex items-center gap-3 mb-4">
            {r.status === "approved" ? <CheckCircle2 className="w-6 h-6 text-pb-cyan" /> : <Hourglass className="w-6 h-6 text-pb-red" />}
            <div>
              <div className="font-display font-black text-2xl uppercase">You're Rostered</div>
              <div className="text-xs text-zinc-500">Entry fee paid · {r.status === "approved" ? "Your team is official!" : "Awaiting coach approval"}</div>
            </div>
            <div className="ml-auto"><Pill color={r.status === "approved" ? "#4ADE80" : "#F59E0B"}>{r.status === "approved" ? "Approved" : "Pending Approval"}</Pill></div>
          </div>
          <div className="grid sm:grid-cols-3 gap-3 text-sm">
            <div className="bg-pb-elevated border border-pb-border p-3">
              <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1">Team Name</div>
              <div className="font-display font-bold uppercase" data-testid="reg-team-name">{r.team_name}</div>
            </div>
            <div className="bg-pb-elevated border border-pb-border p-3">
              <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1">Players</div>
              <div className="font-semibold">{r.user_name}{r.partner_name ? ` + ${r.partner_name}` : " (partner TBD)"}</div>
            </div>
            <div className="bg-pb-elevated border border-pb-border p-3">
              <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1">Division</div>
              <div className="font-semibold">{r.division}</div>
            </div>
            {(r.city || r.facility) && (
              <div className="bg-pb-elevated border border-pb-border p-3 sm:col-span-2">
                <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1">Home Court</div>
                <div className="font-semibold" data-testid="reg-home-court">{[r.city && `${r.city}${r.state ? `, ${r.state}` : ""}`, r.facility].filter(Boolean).join(" · ")}</div>
              </div>
            )}
            {r.levels?.length > 0 && (
              <div className="bg-pb-elevated border border-pb-border p-3">
                <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1">Levels</div>
                <div className="font-semibold text-xs" data-testid="reg-levels-display">{r.levels.join(", ")}</div>
              </div>
            )}
          </div>
        </div>
      ) : (
        <div className="space-y-6">
          {/* Your identity — auto-filled */}
          <div className="bg-pb-surface border border-pb-border p-5 flex items-center gap-4" data-testid="league-reg-identity">
            <Avatar src={user?.avatar} name={user?.name} size={56} />
            <div className="min-w-0">
              <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-0.5">Registering As</div>
              <div className="font-display font-bold text-xl uppercase leading-none">{user?.name}</div>
              {user?.location && <div className="flex items-center gap-1 text-xs text-zinc-500 mt-1"><MapPin className="w-3 h-3 text-pb-cyan" /> {user.location}</div>}
            </div>
            <div className="ml-auto"><Pill color="#A3E635">{(user?.membership || "").toUpperCase()}</Pill></div>
          </div>

          {/* Team name */}
          <div className="bg-pb-surface border border-pb-border p-5">
            <label className="block text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-2">Team Name</label>
            <input data-testid="team-name-input" value={teamName} onChange={(e) => setTeamName(e.target.value)}
              placeholder="e.g. Dink Dynasty" maxLength={40}
              className="w-full bg-pb-elevated border border-pb-border px-4 py-3 text-white placeholder-zinc-600 focus:outline-none focus:border-pb-red font-display font-bold uppercase tracking-wide" />
          </div>

          {/* Location & Facility */}
          <div className="bg-pb-surface border border-pb-border p-5">
            <label className="block text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-3 flex items-center gap-1.5">
              <MapPin className="w-3.5 h-3.5" /> Where You Play
            </label>
            <div className="grid grid-cols-2 gap-3 mb-3">
              <div>
                <label className="block text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-2">City</label>
                <input data-testid="reg-city-input" value={city} onChange={(e) => setCity(e.target.value)} placeholder="e.g. Austin" maxLength={60}
                  className="w-full bg-pb-elevated border border-pb-border px-4 py-3 text-white placeholder-zinc-600 focus:outline-none focus:border-pb-red" />
              </div>
              <div>
                <label className="block text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-2">State</label>
                <input data-testid="reg-state-input" value={stateVal} onChange={(e) => setStateVal(e.target.value)} placeholder="e.g. TX" maxLength={30}
                  className="w-full bg-pb-elevated border border-pb-border px-4 py-3 text-white placeholder-zinc-600 focus:outline-none focus:border-pb-red" />
              </div>
            </div>
            <label className="block text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-2 flex items-center gap-1.5">
              <Building2 className="w-3.5 h-3.5" /> Facility
            </label>
            <input data-testid="reg-facility-input" value={facility} onChange={(e) => setFacility(e.target.value)} placeholder="e.g. Bullpen Pickleball Center" maxLength={80}
              className="w-full bg-pb-elevated border border-pb-border px-4 py-3 text-white placeholder-zinc-600 focus:outline-none focus:border-pb-red" />
          </div>

          {/* Level — check what applies */}
          <div className="bg-pb-surface border border-pb-border p-5">
            <label className="block text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-3">Your Level — check all that apply</label>
            <div className="flex flex-wrap gap-2" data-testid="reg-levels">
              {LEVELS.map((lv) => {
                const on = levels.includes(lv.key);
                const slug = lv.key.toLowerCase().replace(/\s+/g, "-");
                return (
                  <button key={lv.key} type="button" data-testid={`reg-level-${slug}`} onClick={() => toggleLevel(lv.key)}
                    className={`flex items-center gap-1.5 border px-3 py-2 text-[11px] font-bold uppercase tracking-widest transition-all active:scale-95 ${on ? "text-black" : "bg-pb-elevated/40 text-zinc-400 hover:text-zinc-200"}`}
                    style={on ? { background: lv.color, borderColor: lv.color } : { borderColor: "#333" }}>
                    <span className={`w-3.5 h-3.5 border flex items-center justify-center ${on ? "border-black/60" : "border-zinc-600"}`}>
                      {on && <Check className="w-3 h-3" />}
                    </span>
                    {lv.key}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Partner search */}
          <div className="bg-pb-surface border border-pb-border p-5">
            <label className="block text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-2 flex items-center gap-1.5">
              <Users className="w-3.5 h-3.5" /> Your Partner (optional)
            </label>
            {partner ? (
              <div className="flex items-center gap-3 bg-pb-elevated border border-pb-cyan/40 p-3" data-testid="selected-partner">
                <Avatar src={partner.avatar} name={partner.name} size={40} />
                <div>
                  <div className="font-semibold text-sm">{partner.name}</div>
                  {partner.location && <div className="flex items-center gap-1 text-[11px] text-zinc-500"><MapPin className="w-3 h-3 text-pb-cyan" /> {partner.location}</div>}
                </div>
                <button data-testid="remove-partner-btn" onClick={() => setPartner(null)}
                  className="ml-auto w-8 h-8 flex items-center justify-center border border-pb-border hover:border-red-500 hover:text-red-400 transition-colors">
                  <X className="w-4 h-4" />
                </button>
              </div>
            ) : (              <>
                <div className="relative">
                  <Search className="w-4 h-4 text-zinc-500 absolute left-3 top-1/2 -translate-y-1/2" />
                  <input data-testid="partner-search-input" value={query} onChange={(e) => setQuery(e.target.value)}
                    placeholder="Search members by name…"
                    className="w-full bg-pb-elevated border border-pb-border pl-10 pr-4 py-3 text-white placeholder-zinc-600 focus:outline-none focus:border-pb-red" />
                </div>
                {query.trim() && (
                  <div className="mt-2 border border-pb-border divide-y divide-pb-border/50 max-h-56 overflow-y-auto" data-testid="partner-results">
                    {searching ? (
                      <div className="p-3 text-xs text-zinc-500">Searching…</div>
                    ) : results.length === 0 ? (
                      <div className="p-3 text-xs text-zinc-500">No members found for “{query}”.</div>
                    ) : results.map((m) => (
                      <button key={m.id} data-testid={`partner-result-${m.id}`}
                        onClick={() => { setPartner(m); setQuery(""); setResults([]); }}
                        className="w-full flex items-center gap-3 p-3 hover:bg-pb-elevated transition-colors text-left">
                        <Avatar src={m.avatar} name={m.name} size={36} />
                        <div className="min-w-0">
                          <div className="font-semibold text-sm truncate">{m.name}</div>
                          {m.location && <div className="flex items-center gap-1 text-[11px] text-zinc-500"><MapPin className="w-3 h-3 text-pb-cyan" /> {m.location}</div>}
                        </div>
                        {m.membership === "vip" && <span className="ml-auto"><Pill color="#A3E635">VIP</Pill></span>}
                      </button>
                    ))}
                  </div>
                )}
                <p className="text-[11px] text-zinc-600 mt-2">No partner yet? Register solo — you can pair up before the league starts.</p>
              </>
            )}
            {partner && (
              <p className="text-[11px] text-amber-400/90 mt-2" data-testid="league-partner-pay-note">
                {partner.name} pays their own entry fee — they'll get an instant notification + email with a payment
                link and show as PENDING on the roster until they pay.
              </p>
            )}
          </div>

          {/* Fee + Join — admin controlled */}
          {reg?.open === false ? (
            <div className="bg-pb-surface border border-pb-red/40 p-6 text-center" data-testid="league-reg-closed">
              <Lock className="w-7 h-7 text-pb-red mx-auto mb-3" />
              <div className="font-display font-bold uppercase text-xl mb-1">Registration Closed</div>
              <p className="text-sm text-zinc-400">{reg?.closed_message}</p>
            </div>
          ) : (
          <div className="bg-pb-surface border border-pb-red/40 p-5">
            <div className="flex justify-between items-center mb-4">
              <div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">League Entry Fee</div>
                <div className="text-xs text-zinc-500 mt-0.5">Paid securely via Stripe · pending coach approval</div>
              </div>
              <span className="font-display font-black text-3xl text-pb-red" data-testid="league-fee">${(reg?.fee ?? 250).toFixed(2)}</span>
            </div>
            <button data-testid="join-league-btn" onClick={() => joinLeague(false)} disabled={busy || !teamName.trim()}
              className="w-full flex items-center justify-center gap-2 bg-pb-red text-black font-bold uppercase tracking-widest py-4 hover:bg-[#BEF264] active:scale-[0.98] transition-all disabled:opacity-40">
              <Trophy className="w-5 h-5" /> {busy ? "Processing…" : "Join the League"}
            </button>
            <ServiceFeeNote className="text-center mt-1.5" kind="league" />
            {reg?.fee_coins && (
              <button data-testid="join-league-bullion-btn" onClick={() => joinLeague(true)} disabled={busy || !teamName.trim()}
                className="w-full mt-2 border border-purple-400/60 text-purple-300 hover:bg-purple-400/10 font-bold uppercase tracking-widest py-3 text-xs active:scale-[0.98] transition-all disabled:opacity-40">
                🪙 Pay Entry with Coin · {reg.fee_coins} BLC
              </button>
            )}
          </div>
          )}
        </div>
      )}
    </div>
  );
}
